import { useTranslation } from 'react-i18next';
import { CloudOff, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/cn';
import { Loader } from '@/components/common/Loader';

/**
 * Il riquadro che una sezione mostra quando NON ha un elenco da mostrare e il vuoto
 * sarebbe una bugia.
 *
 * 🔴 Nasce dal guasto misurato il 2026-09-06 su `stg-tma` con tutti i tab della Hub
 * accesi: tre pannelli su otto affermavano un vuoto — «No tournaments in this
 * category», «Nothing to watch right now» — su domande **mai poste**, perche' la
 * fonte e' spenta da un flag di build su questo tenant. Un vuoto affermato e' peggio
 * sia dello spinner (che tace) sia dell'errore (che dice di non sapere).
 *
 * Due toni, e la differenza NON e' cosmetica — e' cio' che l'utente puo' farci:
 *  · `unavailable`: la chiamata **non parte**. Riprovare non serve, e il testo non lo
 *    promette. E' uno stato della BUILD, non della rete.
 *  · `error`: la chiamata e' partita e non ha dato una risposta usabile. Riprovare
 *    puo' servire.
 *
 * ⛔ Non unificarli in un solo messaggio «qualcosa e' andato storto»: rifonderebbe
 * esattamente la distinzione che questo file esiste per tenere aperta.
 *
 * Il testo vive in `general.json` (`panelNotice.*`) ed e' RIUSATO, non copiato: due
 * frasi identiche sotto due chiavi divergono alla prima revisione, e poi la stessa app
 * dice due cose diverse dello stesso fatto.
 */
export function PanelNotice({
  tone,
  detail,
  className,
}: {
  tone: 'unavailable' | 'error';
  /** Riga aggiuntiva sotto il testo standard: cosa, di preciso, non ha risposto. */
  detail?: string;
  className?: string;
}) {
  const { t } = useTranslation('general');
  const Icon = tone === 'unavailable' ? CloudOff : TriangleAlert;
  return (
    <div
      role="status"
      className={cn(
        'flex h-full flex-col items-center justify-center gap-3 px-6 py-8 text-center',
        className,
      )}
    >
      <Icon
        aria-hidden
        className={cn('h-8 w-8', tone === 'unavailable' ? 'text-fg-muted' : 'text-danger')}
      />
      <p className="text-base font-bold text-fg">
        {tone === 'unavailable'
          ? t('panelNotice.unavailable.title', { defaultValue: 'Not available on this backend' })
          : t('panelNotice.error.title', { defaultValue: "Couldn't load this section" })}
      </p>
      <p className="max-w-[20rem] text-sm text-fg-muted">
        {tone === 'unavailable'
          ? t('panelNotice.unavailable.body', {
              defaultValue:
                'This section reads from a service this build cannot reach. What you see is not an empty list — it is a list that was never requested.',
            })
          : t('panelNotice.error.body', {
              defaultValue: 'The service did not answer. Nothing here is a statement about your account.',
            })}
      </p>
      {detail && <p className="max-w-[20rem] text-[11px] text-fg-muted opacity-80">{detail}</p>}
    </div>
  );
}

/**
 * L'attesa che dura troppo: lo spinner RESTA — stiamo ancora aspettando davvero — e sotto
 * compare una riga che lo dice.
 *
 * 🔴 Non e' un errore, ed e' esattamente il punto. Misurato su `stg-tma` il 2026-09-06
 * (or45, campionando `animate-spin` a 5/15/25/35 s): le schede muta per 15-25 secondi si
 * sbloccano DA SOLE e dicono l'errore vero. Dichiarare `error` a 12 s avrebbe affermato un
 * fallimento non ancora avvenuto — la stessa colpa del vuoto affermato, commessa dalla
 * difesa contro di esso.
 *
 * ⛔ Questa riga CEDE il posto: appena arriva l'errore vero o arrivano i dati, il verdetto
 * cambia e questo componente sparisce. Non promette che finira' bene, e non dice che e'
 * finita male.
 */
export function PanelWaiting({
  slow,
  /** `false` quando lo spinner lo disegna gia' il chiamante (il cerchio di Ranked). */
  spinner = true,
  className,
}: {
  slow: boolean;
  spinner?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('general');
  return (
    <div className={cn('flex h-full flex-col items-center justify-center gap-3', className)}>
      {spinner && <Loader />}
      {slow && (
        <p className="max-w-[18rem] px-6 text-center text-xs text-fg-muted">
          {t('panelNotice.slow', {
            defaultValue: 'This is taking longer than usual — still waiting for an answer.',
          })}
        </p>
      )}
    </div>
  );
}
